import React, { useEffect, useState, useRef } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button, Badge,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  useDisclosure, Flex, Text, Box, HStack, Select, Tag, Wrap, WrapItem,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useToast,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import * as XLSX from 'xlsx';
import {
  ImmTaller, listarTalleres, eliminarTaller, publicarTaller,
} from '../../../api/immApi';
import { fmt, IMM_COLOR, IMM_COLOR_HOVER, EstatusBadge } from '../_shared';
import FormularioTaller from './FormularioTaller';
import ModalInscripciones from './ModalInscripciones';

const TIPOS = ['Taller', 'Curso', 'Conferencia', 'Platica', 'Actividad', 'Caminata', 'Evento'];

const inscritos = (t: ImmTaller) => t._count?.inscripciones ?? t.inscripciones?.length ?? 0;

const TablaTalleres: React.FC = () => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);

  const verD = useDisclosure();
  const editarD = useDisclosure();
  const nuevoD = useDisclosure();
  const eliminarD = useDisclosure();
  const inscripcionesD = useDisclosure();

  const [data, setData] = useState<ImmTaller[]>([]);
  const [selected, setSelected] = useState<ImmTaller | null>(null);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchData = async () => {
    setCargando(true);
    try {
      setData(await listarTalleres());
    } catch {
      setData([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const estatusUnicos = Array.from(new Set(data.map((t) => t.estatus).filter(Boolean)));

  const datosFiltrados = data.filter((item) => {
    const matchTipo = filtroTipo ? item.tipo === filtroTipo : true;
    const matchEstatus = filtroEstatus ? item.estatus === filtroEstatus : true;
    return matchTipo && matchEstatus;
  });

  const totalPages = Math.ceil(datosFiltrados.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = datosFiltrados.slice(indexPrimero, indexUltimo);

  const handleGuardado = () => { nuevoD.onClose(); editarD.onClose(); fetchData(); };

  const handleEliminarConfirmar = async () => {
    if (!selected) return;
    setProcesando(true);
    try {
      await eliminarTaller(selected.id);
      toast({ title: 'Taller eliminado', status: 'success', duration: 3000 });
      eliminarD.onClose();
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo eliminar.', status: 'error', duration: 4000 });
    } finally {
      setProcesando(false);
    }
  };

  const handleTogglePublicar = async (t: ImmTaller) => {
    try {
      await publicarTaller(t.id, !t.publicadoPortal);
      toast({ title: t.publicadoPortal ? 'Taller despublicado' : 'Taller publicado', status: 'success', duration: 3000 });
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo actualizar.', status: 'error', duration: 4000 });
    }
  };

  const handleExportExcel = () => {
    const filas = datosFiltrados.map((item) => ({
      'Nombre': item.nombre,
      'Tipo': item.tipo,
      'Fecha': fmt(item.fecha),
      'Sede': item.sede || '',
      'Instructor': item.instructor || '',
      'Cupo': item.cupo ?? '',
      'Inscritos': inscritos(item),
      'Publicado': item.publicadoPortal ? 'Sí' : 'No',
      'Estatus': item.estatus,
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Talleres IMM');
    XLSX.writeFile(wb, `Talleres_IMM_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <Box>
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={3} flexWrap="wrap">
          <Select placeholder="Tipo" value={filtroTipo} onChange={(e) => { setFiltroTipo(e.target.value); setCurrentPage(1); }} size="sm" w="160px">
            {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
          <Select placeholder="Estatus" value={filtroEstatus} onChange={(e) => { setFiltroEstatus(e.target.value); setCurrentPage(1); }} size="sm" w="160px">
            {estatusUnicos.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </HStack>
        <HStack spacing={2}>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={handleExportExcel}>Excel</Button>
          <Button size="sm" leftIcon={<AddIcon />} bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={nuevoD.onOpen}>Nuevo taller</Button>
        </HStack>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg={IMM_COLOR}>
            <Tr>
              <Th color="white" fontSize="xs">Nombre</Th>
              <Th color="white" fontSize="xs">Tipo</Th>
              <Th color="white" fontSize="xs">Fecha</Th>
              <Th color="white" fontSize="xs">Sede</Th>
              <Th color="white" fontSize="xs">Cupo</Th>
              <Th color="white" fontSize="xs">Inscritos</Th>
              <Th color="white" fontSize="xs">Portal</Th>
              <Th color="white" fontSize="xs">Estatus</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={9} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : itemsPagina.length === 0 ? (
              <Tr><Td colSpan={9} textAlign="center" py={8}><Text color="gray.400">No se encontraron talleres</Text></Td></Tr>
            ) : (
              itemsPagina.map((item, idx) => (
                <Tr key={item.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                  <Td><Text noOfLines={1} fontSize="sm" fontWeight="medium">{item.nombre}</Text></Td>
                  <Td><Tag size="sm" colorScheme="red" variant="subtle">{item.tipo}</Tag></Td>
                  <Td>{fmt(item.fecha)}</Td>
                  <Td>{item.sede || '—'}</Td>
                  <Td>{item.cupo ?? '—'}</Td>
                  <Td>{inscritos(item)}</Td>
                  <Td>
                    <Badge colorScheme={item.publicadoPortal ? 'green' : 'gray'}>
                      {item.publicadoPortal ? 'Publicado' : 'No'}
                    </Badge>
                  </Td>
                  <Td><EstatusBadge value={item.estatus} /></Td>
                  <Td>
                    <Wrap spacing={1}>
                      <WrapItem><Button size="xs" colorScheme="blue" variant="outline" onClick={() => { setSelected(item); verD.onOpen(); }}>Ver</Button></WrapItem>
                      <WrapItem><Button size="xs" colorScheme="orange" variant="outline" onClick={() => { setSelected(item); editarD.onOpen(); }}>Editar</Button></WrapItem>
                      <WrapItem><Button size="xs" colorScheme="teal" variant="outline" onClick={() => { setSelected(item); inscripcionesD.onOpen(); }}>Inscripciones</Button></WrapItem>
                      <WrapItem>
                        <Button size="xs" colorScheme={item.publicadoPortal ? 'gray' : 'green'} variant="outline" onClick={() => handleTogglePublicar(item)}>
                          {item.publicadoPortal ? 'Despublicar' : 'Publicar'}
                        </Button>
                      </WrapItem>
                      <WrapItem><Button size="xs" colorScheme="red" variant="outline" onClick={() => { setSelected(item); eliminarD.onOpen(); }}>Eliminar</Button></WrapItem>
                    </Wrap>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <Flex mt={4} justify="space-between" align="center">
          <Text fontSize="sm" color="gray.500">Mostrando {indexPrimero + 1}–{Math.min(indexUltimo, datosFiltrados.length)} de {datosFiltrados.length}</Text>
          <HStack spacing={1}>
            <Button size="xs" onClick={() => setCurrentPage(1)} isDisabled={currentPage === 1}>«</Button>
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} isDisabled={currentPage === 1}>‹</Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const page = Math.max(1, currentPage - 2) + i;
              if (page > totalPages) return null;
              return (
                <Button key={page} size="xs" bg={currentPage === page ? IMM_COLOR : undefined} color={currentPage === page ? 'white' : undefined} onClick={() => setCurrentPage(page)}>{page}</Button>
              );
            })}
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} isDisabled={currentPage === totalPages}>›</Button>
            <Button size="xs" onClick={() => setCurrentPage(totalPages)} isDisabled={currentPage === totalPages}>»</Button>
          </HStack>
        </Flex>
      )}

      {/* Ver */}
      <Modal isOpen={verD.isOpen} onClose={verD.onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Detalle del taller</ModalHeader>
          <ModalBody py={5}>{selected && <FormularioTaller modo="view" data={selected} onCancelar={verD.onClose} />}</ModalBody>
        </ModalContent>
      </Modal>

      {/* Editar */}
      <Modal isOpen={editarD.isOpen} onClose={editarD.onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Editar taller</ModalHeader>
          <ModalBody py={5}>{selected && <FormularioTaller modo="edit" data={selected} onGuardado={handleGuardado} onCancelar={editarD.onClose} />}</ModalBody>
        </ModalContent>
      </Modal>

      {/* Nuevo */}
      <Modal isOpen={nuevoD.isOpen} onClose={nuevoD.onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Nuevo taller</ModalHeader>
          <ModalBody py={5}><FormularioTaller modo="new" onGuardado={handleGuardado} onCancelar={nuevoD.onClose} /></ModalBody>
        </ModalContent>
      </Modal>

      {/* Inscripciones */}
      {selected && (
        <ModalInscripciones isOpen={inscripcionesD.isOpen} onClose={inscripcionesD.onClose} taller={selected} />
      )}

      {/* Eliminar */}
      <AlertDialog isOpen={eliminarD.isOpen} leastDestructiveRef={cancelRef} onClose={eliminarD.onClose}>
        <AlertDialogOverlay><AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold" color={IMM_COLOR}>Eliminar taller</AlertDialogHeader>
          <AlertDialogBody>
            El taller <strong>{selected?.nombre}</strong> se eliminará. ¿Continuar?
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={eliminarD.onClose} isDisabled={procesando}>Cancelar</Button>
            <Button colorScheme="red" onClick={handleEliminarConfirmar} ml={3} isLoading={procesando} loadingText="Procesando...">Eliminar</Button>
          </AlertDialogFooter>
        </AlertDialogContent></AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TablaTalleres;
